import React, { useState, useRef, useEffect } from 'react';
import { Stage, Layer, Image as KonvaImage, Text, Rect, Transformer } from 'react-konva';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Eye, RotateCcw, Type, Image as ImageIcon, AlignLeft, AlignCenter, AlignRight, AlignVerticalJustifyStart, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd } from 'lucide-react';
import FieldTooltip from './FieldTooltip';

const CANVAS_WIDTH = 1056; // 11 inches at 96 DPI
const CANVAS_HEIGHT = 816; // 8.5 inches at 96 DPI
const PRINT_SAFE_MARGIN = 36; // 0.375 inches at 96 DPI

// Background component
const BackgroundImage = ({ src }) => {
  const [image, setImage] = useState(null);
  
  useEffect(() => {
    if (src) {
      const img = new window.Image();
      img.src = src;
      // Remove crossOrigin to avoid CORS issues with external images
      // img.crossOrigin = 'anonymous';
      img.onload = () => {
        setImage(img);
      };
      img.onerror = (err) => {
        console.error("Failed to load background image:", src, err);
      };
    }
  }, [src]);
  
  return image ? (
    <KonvaImage
      image={image}
      x={0}
      y={0}
      width={CANVAS_WIDTH}
      height={CANVAS_HEIGHT}
    />
  ) : null;
};

// Logo component
const LogoImage = ({ src, x, y, width, height, onDragEnd, onTransformEnd, isSelected, onSelect }) => {
  const [image, setImage] = useState(null);
  const shapeRef = useRef();
  const trRef = useRef();
  
  useEffect(() => {
    if (src) {
      const img = new window.Image();
      img.src = src;
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setImage(img);
      };
    }
  }, [src]);

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);
  
  if (!image) return null;
  
  return (
    <>
      <KonvaImage
        image={image}
        ref={shapeRef}
        x={x}
        y={y}
        width={width}
        height={height}
        draggable
        onClick={onSelect}
        onTap={onSelect}
        onDragEnd={onDragEnd}
        onTransformEnd={onTransformEnd}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 20 || newBox.height < 20) {
              return oldBox;
            }
            return newBox;
          }}
        />
      )}
    </>
  );
};

// Text element with transformer
const EditableText = ({ element, isSelected, onSelect, onDragEnd, onTransformEnd, isMultiSelected }) => {
  const shapeRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isMultiSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isMultiSelected]);

  return (
    <>
      <Text
        {...element}
        ref={shapeRef}
        offsetX={element.align === 'center' ? element.width / 2 : element.align === 'right' ? element.width : 0}
        onClick={(e) => onSelect(e.evt.shiftKey)}
        onTap={(e) => onSelect(e.evt.shiftKey)}
        onDragEnd={onDragEnd}
        onTransformEnd={onTransformEnd}
        stroke={isMultiSelected ? '#3B82F6' : undefined}
        strokeWidth={isMultiSelected ? 2 : 0}
      />
      {isMultiSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 20) {
              return oldBox;
            }
            return newBox;
          }}
          enabledAnchors={['middle-left', 'middle-right']}
          borderStroke={isSelected ? '#3B82F6' : '#93C5FD'}
          borderStrokeWidth={isSelected ? 3 : 2}
        />
      )}
    </>
  );
};

// Print safe zone indicator
const PrintSafeZone = () => (
  <>
    <Rect
      x={PRINT_SAFE_MARGIN}
      y={PRINT_SAFE_MARGIN}
      width={CANVAS_WIDTH - PRINT_SAFE_MARGIN * 2}
      height={CANVAS_HEIGHT - PRINT_SAFE_MARGIN * 2}
      stroke="#3B82F6"
      strokeWidth={1}
      dash={[5, 5]}
      listening={false}
    />
    <Text
      x={PRINT_SAFE_MARGIN + 10}
      y={PRINT_SAFE_MARGIN + 10}
      text="Print Safe Zone"
      fontSize={12}
      fill="#3B82F6"
      listening={false}
    />
  </>
);

const CertificateEditor = ({ templateBackground, onSave, onModify, logoPreview, instructorName, courseName, recipientName, completionDate, serialNumber, isCustomBackground, initialLayout, readOnly = false }) => {
  const stageRef = useRef(null);
  const [showSafeZone, setShowSafeZone] = useState(true);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  
  // Background image state (for custom backgrounds)
  const [backgroundImage, setBackgroundImage] = useState(null);
  
  useEffect(() => {
    if (isCustomBackground && templateBackground) {
      // Default size: fill to print safe zones
      setBackgroundImage({
        x: PRINT_SAFE_MARGIN,
        y: PRINT_SAFE_MARGIN,
        width: CANVAS_WIDTH - (PRINT_SAFE_MARGIN * 2),
        height: CANVAS_HEIGHT - (PRINT_SAFE_MARGIN * 2)
      });
    } else {
      setBackgroundImage(null);
    }
  }, [templateBackground, isCustomBackground]);
  
  const [elements, setElements] = useState([
    {
      id: 'title',
      type: 'text',
      text: 'Certificate of Achievement',
      x: CANVAS_WIDTH / 2,
      y: 150,
      fontSize: 48,
      fontFamily: 'Playfair Display',
      fontStyle: 'bold',
      fill: '#0F172A',
      align: 'center',
      width: 700,
      draggable: true
    },
    {
      id: 'subtitle',
      type: 'text',
      text: 'This certificate is proudly presented to',
      x: CANVAS_WIDTH / 2,
      y: 240,
      fontSize: 16,
      fontFamily: 'Manrope',
      fill: '#64748B',
      align: 'center',
      width: 500,
      draggable: true
    },
    {
      id: 'recipient',
      type: 'text',
      text: '[Recipient Name]',
      x: CANVAS_WIDTH / 2,
      y: 300,
      fontSize: 36,
      fontFamily: 'Playfair Display',
      fontStyle: 'bold',
      fill: '#0F172A',
      align: 'center',
      width: 600,
      draggable: true
    },
    {
      id: 'completion',
      type: 'text',
      text: 'For successful completion of',
      x: CANVAS_WIDTH / 2,
      y: 360,
      fontSize: 14,
      fontFamily: 'Manrope',
      fill: '#64748B',
      align: 'center',
      width: 500,
      draggable: true
    },
    {
      id: 'course',
      type: 'text',
      text: '[Course Name]',
      x: CANVAS_WIDTH / 2,
      y: 390,
      fontSize: 20,
      fontFamily: 'Playfair Display',
      fontStyle: 'bold',
      fill: '#0F172A',
      align: 'center',
      width: 600,
      draggable: true
    },
    {
      id: 'date',
      type: 'text',
      text: 'Date: [Date]',
      x: 80,
      y: 700,
      fontSize: 14,
      fontFamily: 'Manrope',
      fill: '#000000',
      align: 'left',
      width: 200,
      draggable: true
    },
    {
      id: 'serial',
      type: 'text',
      text: 'Serial: [Serial Number]',
      x: 80,
      y: 730,
      fontSize: 12,
      fontFamily: 'JetBrains Mono',
      fill: '#000000',
      align: 'left',
      width: 250,
      draggable: true
    },
    {
      id: 'instructor',
      type: 'text',
      text: instructorName || '[Instructor Name]',
      x: CANVAS_WIDTH - 280,
      y: 700,
      fontSize: 14,
      fontFamily: 'Manrope',
      fontStyle: 'bold',
      fill: '#0F172A',
      align: 'right',
      width: 200,
      draggable: true
    }
  ]);

  const [logo, setLogo] = useState(logoPreview ? {
    x: CANVAS_WIDTH / 2 - 75,
    y: 50,
    width: 150,
    height: 75
  } : null);

  const [selectedId, setSelectedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]); // For multi-select
  const [history, setHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(-1);
  const [shouldSaveHistory, setShouldSaveHistory] = useState(false);

  useEffect(() => {
    if (logoPreview && !logo) {
      setLogo({
        x: CANVAS_WIDTH / 2 - 75,
        y: 50,
        width: 150,
        height: 75
      });
    }
  }, [logoPreview]);

  // Helper function to replace placeholders with actual data
  const replacePlaceholders = (text) => {
    if (!text) return text;
    let result = text;
    
    // Replace placeholders with actual data (for read-only mode)
    if (readOnly) {
      if (recipientName) {
        result = result.replace(/\[Recipient Name\]/gi, recipientName);
        result = result.replace(/^Recipient Name$/gi, recipientName);
      }
      if (courseName) {
        result = result.replace(/\[Course Name\]/gi, courseName);
        result = result.replace(/^Course Name$/gi, courseName);
      }
      if (instructorName) {
        // Replace all variations of instructor name placeholders
        result = result.replace(/\[Instructor Name\]/gi, instructorName);
        result = result.replace(/^Instructor Name$/gim, instructorName);
        result = result.replace(/Instructor Name/g, instructorName);
        result = result.replace(/instructor name/gi, instructorName);
      }
      if (completionDate) {
        result = result.replace(/\[Date\]/gi, completionDate);
        result = result.replace(/\[Completion Date\]/gi, completionDate);
        result = result.replace(/^Date$/gi, completionDate);
      }
      if (serialNumber) {
        result = result.replace(/\[Serial Number\]/gi, serialNumber);
        result = result.replace(/\[Serial\]/gi, serialNumber);
        result = result.replace(/^Serial Number$/gi, serialNumber);
      }
    } else {
      // In edit mode, replace with dynamic field markers if data is available
      if (instructorName) {
        result = result.replace(/\[Instructor Name\]/gi, instructorName);
        result = result.replace(/^Instructor Name$/gim, instructorName);
        result = result.replace(/Instructor Name/g, instructorName);
      }
      if (courseName) {
        result = result.replace(/\[Course Name\]/gi, courseName);
        result = result.replace(/^Course Name$/gim, courseName);
      }
    }
    
    return result;
  };

  // Load initial layout if provided (for editing existing templates)
  useEffect(() => {
    if (initialLayout && !layoutLoaded) {
      if (initialLayout.elements) {
        // Ensure all elements are draggable when loading and replace placeholders
        const draggableElements = initialLayout.elements.map(el => ({
          ...el,
          text: replacePlaceholders(el.text),
          draggable: !readOnly // Only draggable if not in read-only mode
        }));
        setElements(draggableElements);
      }
      if (initialLayout.logo) {
        setLogo(initialLayout.logo);
      }
      if (initialLayout.backgroundImage) {
        setBackgroundImage(initialLayout.backgroundImage);
      }
      setLayoutLoaded(true);
    }
  }, [initialLayout, layoutLoaded, readOnly, recipientName, courseName, instructorName, completionDate, serialNumber]);

  // Save initial state to history when elements are loaded
  useEffect(() => {
    if (elements.length > 0 && history.length === 0) {
      const initialState = {
        elements: JSON.parse(JSON.stringify(elements)),
        logo: logo ? JSON.parse(JSON.stringify(logo)) : null,
        backgroundImage: backgroundImage ? JSON.parse(JSON.stringify(backgroundImage)) : null
      };
      setHistory([initialState]);
      setHistoryStep(0);
    }
  }, [elements.length]);

  const handleDragEnd = (id, e) => {
    if (id === 'logo') {
      setLogo(prev => ({ ...prev, x: e.target.x(), y: e.target.y() }));
    } else if (id === 'background') {
      setBackgroundImage(prev => ({ ...prev, x: e.target.x(), y: e.target.y() }));
    } else {
      setElements(prev =>
        prev.map(el =>
          el.id === id ? { ...el, x: e.target.x(), y: e.target.y() } : el
        )
      );
    }
    setShouldSaveHistory(true);
    if (onModify) onModify();
  };

  const handleTransformEnd = (id, e) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    if (id === 'logo') {
      setLogo(prev => ({
        ...prev,
        x: node.x(),
        y: node.y(),
        width: Math.max(20, node.width() * scaleX),
        height: Math.max(20, node.height() * scaleY)
      }));
    } else if (id === 'background') {
      setBackgroundImage(prev => ({
        ...prev,
        x: node.x(),
        y: node.y(),
        width: Math.max(100, node.width() * scaleX),
        height: Math.max(100, node.height() * scaleY)
      }));
    } else {
      setElements(prev =>
        prev.map(el =>
          el.id === id
            ? {
                ...el,
                x: node.x(),
                y: node.y(),
                width: Math.max(20, node.width() * scaleX),
                fontSize: Math.max(8, el.fontSize * scaleY)
              }
            : el
        )
      );
    }

    node.scaleX(1);
    node.scaleY(1);
    setShouldSaveHistory(true);
    if (onModify) onModify();
  };

  const handleSelect = (id) => {
    setSelectedId(id);
  };

  const handleDeselect = () => {
    setSelectedId(null);
  };

  const updateSelectedElement = (property, value) => {
    if (onModify) onModify();
    setElements(prev =>
      prev.map(el =>
        el.id === selectedId ? { ...el, [property]: value } : el
      )
    );
  };

  // Multi-select handlers
  const handleMultiSelect = (id, isShiftKey) => {
    if (isShiftKey) {
      // Add to selection
      if (!selectedIds.includes(id)) {
        setSelectedIds([...selectedIds, id]);
      }
    } else {
      // Single select
      setSelectedId(id);
      setSelectedIds([id]);
    }
  };

  const clearSelection = () => {
    setSelectedId(null);
    setSelectedIds([]);
  };

  // Save to history after state changes
  useEffect(() => {
    if (shouldSaveHistory && elements.length > 0) {
      const currentState = {
        elements: JSON.parse(JSON.stringify(elements)),
        logo: logo ? JSON.parse(JSON.stringify(logo)) : null,
        backgroundImage: backgroundImage ? JSON.parse(JSON.stringify(backgroundImage)) : null
      };
      
      // Remove any future history if we're not at the end
      const newHistory = history.slice(0, historyStep + 1);
      newHistory.push(currentState);
      
      // Limit history to 50 steps
      if (newHistory.length > 50) {
        newHistory.shift();
        setHistory(newHistory);
      } else {
        setHistory(newHistory);
        setHistoryStep(newHistory.length - 1);
      }
      
      setShouldSaveHistory(false);
    }
  }, [shouldSaveHistory, elements, logo, backgroundImage]);

  const undo = () => {
    if (historyStep > 0) {
      const prevState = history[historyStep - 1];
      setElements(prevState.elements);
      setLogo(prevState.logo);
      setBackgroundImage(prevState.backgroundImage);
      setHistoryStep(historyStep - 1);
    }
  };

  const redo = () => {
    if (historyStep < history.length - 1) {
      const nextState = history[historyStep + 1];
      setElements(nextState.elements);
      setLogo(nextState.logo);
      setBackgroundImage(nextState.backgroundImage);
      setHistoryStep(historyStep + 1);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        setHistoryStep(prev => {
          if (prev > 0) {
            const prevState = history[prev - 1];
            setElements(prevState.elements);
            setLogo(prevState.logo);
            setBackgroundImage(prevState.backgroundImage);
            return prev - 1;
          }
          return prev;
        });
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        setHistoryStep(prev => {
          if (prev < history.length - 1) {
            const nextState = history[prev + 1];
            setElements(nextState.elements);
            setLogo(nextState.logo);
            setBackgroundImage(nextState.backgroundImage);
            return prev + 1;
          }
          return prev;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [history]);

  // Alignment functions
  const alignToPage = (alignment) => {
    if (selectedIds.length === 0) return;

    setElements(prev =>
      prev.map(el => {
        if (selectedIds.includes(el.id)) {
          let updates = {};
          switch (alignment) {
            case 'left':
              updates.x = PRINT_SAFE_MARGIN + el.width / 2;
              break;
            case 'center':
              updates.x = CANVAS_WIDTH / 2;
              break;
            case 'right':
              updates.x = CANVAS_WIDTH - PRINT_SAFE_MARGIN - el.width / 2;
              break;
            case 'top':
              updates.y = PRINT_SAFE_MARGIN + el.fontSize;
              break;
            case 'middle':
              updates.y = CANVAS_HEIGHT / 2;
              break;
            case 'bottom':
              updates.y = CANVAS_HEIGHT - PRINT_SAFE_MARGIN;
              break;
          }
          return { ...el, ...updates };
        }
        return el;
      })
    );
    setShouldSaveHistory(true);
  };

  const alignSelected = (alignment) => {
    if (selectedIds.length < 2) return;

    const selectedElements = elements.filter(el => selectedIds.includes(el.id));
    let referenceValue;

    switch (alignment) {
      case 'left':
        // Find the leftmost X position
        const leftmostX = Math.min(...selectedElements.map(el => {
          // Account for alignment: left-aligned text starts at x, center at x-width/2, right at x-width
          if (el.align === 'center') return el.x - el.width / 2;
          if (el.align === 'right') return el.x - el.width;
          return el.x; // left-aligned
        }));
        
        setElements(prev =>
          prev.map(el => {
            if (selectedIds.includes(el.id)) {
              // Set x based on element's alignment
              let newX = leftmostX;
              if (el.align === 'center') newX = leftmostX + el.width / 2;
              if (el.align === 'right') newX = leftmostX + el.width;
              return { ...el, x: newX };
            }
            return el;
          })
        );
        break;
      case 'center':
        referenceValue = selectedElements.reduce((sum, el) => sum + el.x, 0) / selectedElements.length;
        setElements(prev =>
          prev.map(el =>
            selectedIds.includes(el.id) ? { ...el, x: referenceValue } : el
          )
        );
        break;
      case 'right':
        // Find the rightmost X position
        const rightmostX = Math.max(...selectedElements.map(el => {
          if (el.align === 'center') return el.x + el.width / 2;
          if (el.align === 'right') return el.x;
          return el.x + el.width; // left-aligned
        }));
        
        setElements(prev =>
          prev.map(el => {
            if (selectedIds.includes(el.id)) {
              let newX = rightmostX;
              if (el.align === 'center') newX = rightmostX - el.width / 2;
              if (el.align === 'left') newX = rightmostX - el.width;
              return { ...el, x: newX };
            }
            return el;
          })
        );
        break;
      case 'top':
        // Align to the topmost Y position (considering text starts at Y baseline)
        referenceValue = Math.min(...selectedElements.map(el => el.y));
        setElements(prev =>
          prev.map(el =>
            selectedIds.includes(el.id) ? { ...el, y: referenceValue } : el
          )
        );
        break;
      case 'middle':
        // Align to average Y position
        referenceValue = selectedElements.reduce((sum, el) => sum + el.y, 0) / selectedElements.length;
        setElements(prev =>
          prev.map(el =>
            selectedIds.includes(el.id) ? { ...el, y: referenceValue } : el
          )
        );
        break;
      case 'bottom':
        // Align to the bottommost Y position
        referenceValue = Math.max(...selectedElements.map(el => el.y));
        setElements(prev =>
          prev.map(el =>
            selectedIds.includes(el.id) ? { ...el, y: referenceValue } : el
          )
        );
        break;
    }
    setShouldSaveHistory(true);
  };

  const distributeSelected = (direction) => {
    if (selectedIds.length < 3) return;

    const selectedElements = elements.filter(el => selectedIds.includes(el.id));
    
    if (direction === 'horizontal') {
      selectedElements.sort((a, b) => a.x - b.x);
      const minX = selectedElements[0].x;
      const maxX = selectedElements[selectedElements.length - 1].x;
      const gap = (maxX - minX) / (selectedElements.length - 1);
      
      setElements(prev =>
        prev.map(el => {
          const idx = selectedElements.findIndex(sel => sel.id === el.id);
          if (idx > 0 && idx < selectedElements.length - 1) {
            return { ...el, x: minX + gap * idx };
          }
          return el;
        })
      );
    } else if (direction === 'vertical') {
      selectedElements.sort((a, b) => a.y - b.y);
      const minY = selectedElements[0].y;
      const maxY = selectedElements[selectedElements.length - 1].y;
      const gap = (maxY - minY) / (selectedElements.length - 1);
      
      setElements(prev =>
        prev.map(el => {
          const idx = selectedElements.findIndex(sel => sel.id === el.id);
          if (idx > 0 && idx < selectedElements.length - 1) {
            return { ...el, y: minY + gap * idx };
          }
          return el;
        })
      );
    }
  };

  const handleSave = () => {
    const templateData = {
      elements: elements.map(el => ({
        id: el.id,
        type: el.type,
        text: el.text,
        x: el.x,
        y: el.y,
        width: el.width,
        fontSize: el.fontSize,
        fontFamily: el.fontFamily,
        fontStyle: el.fontStyle,
        fill: el.fill,
        align: el.align
      })),
      logo: logo ? {
        x: logo.x,
        y: logo.y,
        width: logo.width,
        height: logo.height
      } : null,
      backgroundImage: backgroundImage ? {
        x: backgroundImage.x,
        y: backgroundImage.y,
        width: backgroundImage.width,
        height: backgroundImage.height
      } : null
    };
    onSave(templateData);
  };

  const handleReset = () => {
    setElements([
      {
        id: 'title',
        type: 'text',
        text: 'Certificate of Achievement',
        x: CANVAS_WIDTH / 2,
        y: 150,
        fontSize: 48,
        fontFamily: 'Playfair Display',
        fontStyle: 'bold',
        fill: '#0F172A',
        align: 'center',
        width: 700,
        draggable: true
      },
      {
        id: 'subtitle',
        type: 'text',
        text: 'This certificate is proudly presented to',
        x: CANVAS_WIDTH / 2,
        y: 240,
        fontSize: 16,
        fontFamily: 'Manrope',
        fill: '#64748B',
        align: 'center',
        width: 500,
        draggable: true
      },
      {
        id: 'recipient',
        type: 'text',
        text: '[Recipient Name]',
        x: CANVAS_WIDTH / 2,
        y: 300,
        fontSize: 36,
        fontFamily: 'Playfair Display',
        fontStyle: 'bold',
        fill: '#0F172A',
        align: 'center',
        width: 600,
        draggable: true
      },
      {
        id: 'completion',
        type: 'text',
        text: 'For successful completion of',
        x: CANVAS_WIDTH / 2,
        y: 360,
        fontSize: 14,
        fontFamily: 'Manrope',
        fill: '#64748B',
        align: 'center',
        width: 500,
        draggable: true
      },
      {
        id: 'course',
        type: 'text',
        text: '[Course Name]',
        x: CANVAS_WIDTH / 2,
        y: 390,
        fontSize: 20,
        fontFamily: 'Playfair Display',
        fontStyle: 'bold',
        fill: '#0F172A',
        align: 'center',
        width: 600,
        draggable: true
      },
      {
        id: 'date',
        type: 'text',
        text: 'Date: [Date]',
        x: 80,
        y: 700,
        fontSize: 14,
        fontFamily: 'Manrope',
        fill: '#64748B',
        align: 'left',
        width: 200,
        draggable: true
      },
      {
        id: 'serial',
        type: 'text',
        text: 'Serial: [Serial Number]',
        x: 80,
        y: 730,
        fontSize: 12,
        fontFamily: 'JetBrains Mono',
        fill: '#94A3B8',
        align: 'left',
        width: 250,
        draggable: true
      },
      {
        id: 'instructor',
        type: 'text',
        text: instructorName || '[Instructor Name]',
        x: CANVAS_WIDTH - 280,
        y: 700,
        fontSize: 14,
        fontFamily: 'Manrope',
        fontStyle: 'bold',
        fill: '#0F172A',
        align: 'right',
        width: 200,
        draggable: true
      }
    ]);
    setLogo(logoPreview ? {
      x: CANVAS_WIDTH / 2 - 75,
      y: 50,
      width: 150,
      height: 75
    } : null);
    setSelectedId(null);
  };

  const selectedElement = elements.find(el => el.id === selectedId);
  const isLogoSelected = selectedId === 'logo';
  const isBackgroundSelected = selectedId === 'background';

  return (
    <div className={`flex flex-col ${readOnly ? '' : 'lg:flex-row'} gap-6`} data-testid="certificate-editor">
      {/* Left Side - Canvas */}
      <div className={`${readOnly ? 'w-full' : 'flex-1'}`}>
        {/* Toolbar - Only show in edit mode */}
        {!readOnly && (
          <div className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2">
              <Button
                onClick={handleSave}
                className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-4 py-2"
                data-testid="save-template-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Template
              </Button>
              <Button
                onClick={() => setShowSafeZone(!showSafeZone)}
                variant="outline"
                className="rounded-lg px-4 py-2"
                data-testid="toggle-safe-zone-btn"
              >
                <Eye className="h-4 w-4 mr-2" />
                {showSafeZone ? 'Hide' : 'Show'} Safe Zone
              </Button>
              <Button
                onClick={handleReset}
                variant="outline"
                className="rounded-lg px-4 py-2"
                data-testid="reset-btn"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
              <Button
                onClick={undo}
                variant="outline"
                className="rounded-lg px-4 py-2"
                disabled={historyStep <= 0}
                data-testid="undo-btn"
              >
                Undo (Ctrl+Z)
              </Button>
              <Button
                onClick={redo}
                variant="outline"
                className="rounded-lg px-4 py-2"
                disabled={historyStep >= history.length - 1}
                data-testid="redo-btn"
              >
                Redo (Ctrl+Y)
              </Button>
            </div>
          </div>
        )}

        {/* Canvas Area */}
        <div className={`bg-slate-100 border border-slate-200 rounded-lg ${readOnly ? 'p-0' : 'p-8'} flex items-center justify-center ${readOnly ? 'overflow-hidden' : ''}`}>
          <div className="bg-white shadow-xl" style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}>
              <Stage
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
                ref={stageRef}
                onMouseDown={(e) => {
                  if (e.target === e.target.getStage()) {
                    clearSelection();
                  }
                }}
                onTouchStart={(e) => {
                  if (e.target === e.target.getStage()) {
                    clearSelection();
                  }
                }}
              >
              <Layer>
                {/* Background - Static or Editable */}
                {templateBackground && !isCustomBackground && <BackgroundImage src={templateBackground} />}
                
                {/* Editable Custom Background */}
                {templateBackground && isCustomBackground && backgroundImage && !readOnly && (
                  <LogoImage
                    src={templateBackground}
                    x={backgroundImage.x}
                    y={backgroundImage.y}
                    width={backgroundImage.width}
                    height={backgroundImage.height}
                    isSelected={isBackgroundSelected}
                    onSelect={() => handleSelect('background')}
                    onDragEnd={(e) => handleDragEnd('background', e)}
                    onTransformEnd={(e) => handleTransformEnd('background', e)}
                  />
                )}
                
                {/* Static background in read-only mode */}
                {templateBackground && isCustomBackground && backgroundImage && readOnly && (
                  <BackgroundImage src={templateBackground} />
                )}
                
                {/* Print Safe Zone */}
                {showSafeZone && !readOnly && <PrintSafeZone />}
                
                {/* Logo */}
                {logoPreview && logo && !readOnly && (
                  <LogoImage
                    src={logoPreview}
                    x={logo.x}
                    y={logo.y}
                    width={logo.width}
                    height={logo.height}
                    isSelected={isLogoSelected}
                    onSelect={() => handleSelect('logo')}
                    onDragEnd={(e) => handleDragEnd('logo', e)}
                    onTransformEnd={(e) => handleTransformEnd('logo', e)}
                  />
                )}
                
                {/* Logo in read-only mode */}
                {logoPreview && logo && readOnly && (
                  <KonvaImage
                    image={(() => {
                      const img = new window.Image();
                      img.src = logoPreview;
                      return img;
                    })()}
                    x={logo.x}
                    y={logo.y}
                    width={logo.width}
                    height={logo.height}
                    listening={false}
                  />
                )}
                
                {/* Text Elements */}
                {elements.map((element) => 
                  readOnly ? (
                    <Text
                      key={element.id}
                      {...element}
                      offsetX={element.align === 'center' ? element.width / 2 : element.align === 'right' ? element.width : 0}
                      listening={false}
                    />
                  ) : (
                    <EditableText
                      key={element.id}
                      element={element}
                      isSelected={selectedId === element.id}
                      isMultiSelected={selectedIds.includes(element.id)}
                      onSelect={(isShiftKey) => handleMultiSelect(element.id, isShiftKey)}
                      onDragEnd={(e) => handleDragEnd(element.id, e)}
                      onTransformEnd={(e) => handleTransformEnd(element.id, e)}
                    />
                  )
                )}
              </Layer>
            </Stage>
          </div>
        </div>
      </div>

      {/* Right Side - Properties Panel - Only show in edit mode */}
      {!readOnly && (
        <div className="w-full lg:w-96">
          <div className="bg-white border border-slate-200 rounded-lg p-6 sticky top-6">
          <h3 className="text-2xl font-semibold text-slate-900 mb-6" style={{fontFamily: 'Playfair Display'}}>
            {isBackgroundSelected ? (
              <><ImageIcon className="h-6 w-6 inline mr-2" />Background Properties</>
            ) : isLogoSelected ? (
              <><ImageIcon className="h-6 w-6 inline mr-2" />Logo Properties</>
            ) : selectedElement ? (
              <><Type className="h-6 w-6 inline mr-2" />Text Properties</>
            ) : (
              'Select an element'
            )}
          </h3>

          {selectedElement && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-amber-900">
                  <strong>Note:</strong> You can edit both the text content and the styling. Use placeholders like [Recipient Name], [Course Name], [Date], [Serial Number], and [Instructor Name] for dynamic fields.
                </p>
              </div>

              {/* Text Content Editor */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Text Content
                  <FieldTooltip 
                    fieldName="Text Content"
                    description="Edit the actual text that will appear on the certificate. Use placeholders in brackets like [Recipient Name] for dynamic fields that will be filled when issuing certificates."
                  />
                </Label>
                <textarea
                  value={selectedElement.text}
                  onChange={(e) => updateSelectedElement('text', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter text content..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Font Size
                  <FieldTooltip 
                    fieldName="Font Size"
                    description="Adjust the size of this text element. Larger sizes (36-48) work well for titles and recipient names, while smaller sizes (12-16) are better for details like dates and serial numbers."
                  />
                </Label>
                <Input
                  type="number"
                  value={Math.round(selectedElement.fontSize)}
                  onChange={(e) => updateSelectedElement('fontSize', parseInt(e.target.value))}
                  min={8}
                  max={100}
                  className="h-10 rounded-lg border-slate-200"
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Font Family
                  <FieldTooltip 
                    fieldName="Font Family"
                    description="Choose the typeface for this text element. Elegant Script fonts are perfect for recipient names, Classic Serif fonts for titles, and Modern Sans for details. This styling will apply to all certificates created with this template."
                  />
                </Label>
                <Select value={selectedElement.fontFamily} onValueChange={(value) => updateSelectedElement('fontFamily', value)}>
                  <SelectTrigger className="h-10 rounded-lg border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[400px]">
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase">Elegant Script</div>
                    <SelectItem value="Great Vibes" style={{fontFamily: 'Great Vibes'}}>Great Vibes</SelectItem>
                    <SelectItem value="Allura" style={{fontFamily: 'Allura'}}>Allura</SelectItem>
                    <SelectItem value="Sacramento" style={{fontFamily: 'Sacramento'}}>Sacramento</SelectItem>
                    <SelectItem value="Dancing Script" style={{fontFamily: 'Dancing Script'}}>Dancing Script</SelectItem>
                    <SelectItem value="Pacifico" style={{fontFamily: 'Pacifico'}}>Pacifico</SelectItem>
                    
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase mt-2">Classic Serif</div>
                    <SelectItem value="Playfair Display" style={{fontFamily: 'Playfair Display'}}>Playfair Display</SelectItem>
                    <SelectItem value="Cinzel" style={{fontFamily: 'Cinzel'}}>Cinzel</SelectItem>
                    <SelectItem value="Cormorant Garamond" style={{fontFamily: 'Cormorant Garamond'}}>Cormorant Garamond</SelectItem>
                    <SelectItem value="Libre Baskerville" style={{fontFamily: 'Libre Baskerville'}}>Libre Baskerville</SelectItem>
                    <SelectItem value="Georgia" style={{fontFamily: 'Georgia'}}>Georgia</SelectItem>
                    
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase mt-2">Modern Sans</div>
                    <SelectItem value="Montserrat" style={{fontFamily: 'Montserrat'}}>Montserrat</SelectItem>
                    <SelectItem value="Manrope" style={{fontFamily: 'Manrope'}}>Manrope</SelectItem>
                    <SelectItem value="Arial" style={{fontFamily: 'Arial'}}>Arial</SelectItem>
                    
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase mt-2">Monospace</div>
                    <SelectItem value="JetBrains Mono" style={{fontFamily: 'JetBrains Mono'}}>JetBrains Mono</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Color
                  <FieldTooltip 
                    fieldName="Text Color"
                    description="Select the color for this text element. Dark colors (#0F172A, #000000) work well for main content, while lighter colors (#64748B) are good for supporting text. Ensure good contrast with your background."
                  />
                </Label>
                <Input
                  type="color"
                  value={selectedElement.fill}
                  onChange={(e) => updateSelectedElement('fill', e.target.value)}
                  className="h-10 rounded-lg border-slate-200"
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Alignment
                  <FieldTooltip 
                    fieldName="Text Alignment"
                    description="Choose how the text is aligned. Center works well for titles and recipient names, while left or right alignment is better for dates, signatures, and details."
                  />
                </Label>
                <Select value={selectedElement.align} onValueChange={(value) => updateSelectedElement('align', value)}>
                  <SelectTrigger className="h-10 rounded-lg border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="center">Center</SelectItem>
                    <SelectItem value="right">Right</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Multi-Select Alignment Tools */}
          {selectedIds.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-blue-900 font-medium">
                  {selectedIds.length} item{selectedIds.length > 1 ? 's' : ''} selected
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={clearSelection}
                  className="text-xs h-7 mt-2 w-full"
                >
                  Clear Selection
                </Button>
              </div>

              {/* Align to Page */}
              <div className="mb-4">
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Align to Page
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  <Button size="sm" variant="outline" onClick={() => alignToPage('left')} className="h-9" title="Align Left">
                    <AlignLeft className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => alignToPage('center')} className="h-9" title="Align Center">
                    <AlignCenter className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => alignToPage('right')} className="h-9" title="Align Right">
                    <AlignRight className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => alignToPage('top')} className="h-9" title="Align Top">
                    <AlignVerticalJustifyStart className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => alignToPage('middle')} className="h-9" title="Align Middle">
                    <AlignVerticalJustifyCenter className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => alignToPage('bottom')} className="h-9" title="Align Bottom">
                    <AlignVerticalJustifyEnd className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Align Selected (only show if 2+ items) */}
              {selectedIds.length >= 2 && (
                <div className="mb-4">
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                    Align Selected Items
                  </Label>
                  <div className="grid grid-cols-3 gap-2">
                    <Button size="sm" variant="outline" onClick={() => alignSelected('left')} className="h-9" title="Align Left Edges">
                      L
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => alignSelected('center')} className="h-9" title="Align Centers">
                      C
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => alignSelected('right')} className="h-9" title="Align Right Edges">
                      R
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => alignSelected('top')} className="h-9" title="Align Top Edges">
                      T
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => alignSelected('middle')} className="h-9" title="Align Middles">
                      M
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => alignSelected('bottom')} className="h-9" title="Align Bottom Edges">
                      B
                    </Button>
                  </div>
                </div>
              )}

              {/* Distribute (only show if 3+ items) */}
              {selectedIds.length >= 3 && (
                <div className="mb-4">
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                    Distribute
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button size="sm" variant="outline" onClick={() => distributeSelected('horizontal')} className="h-9" title="Distribute Horizontally">
                      ↔ Horizontal
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => distributeSelected('vertical')} className="h-9" title="Distribute Vertically">
                      ↕ Vertical
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {isBackgroundSelected && (
            <div className="text-sm text-slate-600 space-y-2">
              <p><strong>Background Image</strong></p>
              <p>Drag to reposition the background.</p>
              <p>Use corner handles to stretch and resize.</p>
              <p className="text-xs text-blue-600 mt-2">Tip: The background starts at the print safe zone margins by default.</p>
            </div>
          )}

          {isLogoSelected && (
            <div className="text-sm text-slate-600">
              <p className="mb-2">Drag the logo to reposition it.</p>
              <p>Use the corner handles to resize.</p>
            </div>
          )}

          {!selectedElement && !isLogoSelected && !isBackgroundSelected && (
            <p className="text-xl text-slate-800 leading-loose font-medium mb-4">Click any element (text, logo, or background) to edit its properties.</p>
          )}
        </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mt-6">
            <p className="text-lg text-blue-900 leading-loose">
              <strong>Tips:</strong> Click to select, drag to move, use corner handles to resize. Keep content inside the blue safe zone for printing.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateEditor;
